import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { scrollTo } from '@/components/hooks/hash';
import { useMaintenanceList } from '@/components/hooks/useMaintenanceList';
import CustomMetatag from '@/components/layouts/custom-metatag';
import Layout from '@/components/layouts/lcv';
import { MaintenanceList } from '@/components/sections/service/maintenance-list';
import type { MaintenanceListSearchFilterOptionItem } from '@/components/sections/service/maintenance-list/search-filter';
import OfferCTA, { IOffersCTA } from '@/components/sections/shared/offers-cta';
import { normalisePathname } from '@/components/shared/utility/router';
import { SelectOptionStep } from '@/const/service/maintenance-list/lcv';
import {
  MENU_LCV_SPECIAL_OFFER,
  MENU_LCV_SPECIAL_OFFER_SERVICE,
} from '@/contents/lcv/link';
import {
  MAINTENANCE_LIST,
  META_TAG_DATA,
} from '@/contents/lcv/service/maintenance-list';
import { getScheduleSelectOptionValue } from '@/contents/useMaintenanceList';
import {
  getLcvFirstOptionUniqueKey,
  getLcvSecondOptionUniqueKey,
  getLcvServiceMaintenancePageUrlFormat,
} from '@/contents/useMaintenanceList/lcv';
import logger from '@/lib/logger';
import { getServiceOffers } from '@/services/contentstack';
import {
  getAllLcvMaintenanceList,
  getLcvMaintenanceListByPageUrl,
  getLcvMaintenanceOptions,
} from '@/services/contentstack/services/maintenance-service';
import {
  ContentstackLcvMaintenanceListResponse,
  MaintenanceOptionGroupedItem,
  RegularMaintenaceSchedule,
} from '@/types/services/contentstack/services/maintenance-service';

export async function getStaticPaths() {
  const entries = await getAllLcvMaintenanceList();
  const paths = entries.map((entry) => ({
    params: {
      url: getLcvServiceMaintenancePageUrlFormat(entry),
    },
  }));
  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  try {
    const data = await getLcvMaintenanceListByPageUrl(params.url);
    if (!data) {
      logger.error('Error getStaticProps data empty:', params.url);
      return {
        // Note: Redirect is not working
        redirect: {
          destination: '/service/maintenance-list',
          permanent: false,
        },
      };
    }
    const selectModelValue = getLcvFirstOptionUniqueKey(data);
    const selectEngineValue = getLcvSecondOptionUniqueKey(data);
    const initialSelectValues = [selectModelValue, selectEngineValue, ''];
    const options = await getLcvMaintenanceOptions();
    const res = await getServiceOffers('lcv', 8);
    const offers = res.data.entries.map((o) => ({
      name: o.name,
      img: {
        url: o.img && o.img.url,
      },
      category: o.category.map((c) => ({
        url: c.url,
      })),
      url: o.url,
    }));
    return {
      props: JSON.parse(
        JSON.stringify({
          data,
          initialSelectValues,
          options,
          offers,
          url: params.url,
        })
      ),
    };
  } catch (error) {
    logger.error('Error getStaticProps:', error);
    if (error?.response) {
      logger.error('Error getStaticProps error.response: ', error.response);
    }
    return {
      // Note: Redirect is not working
      redirect: {
        destination: '/service/maintenance-list',
        permanent: false,
      },
    };
  }
}

const FooterLCVGroup = dynamic(
  () => import('@/components/layouts/lcv/footer-group'),
  {
    ssr: false,
  }
);

interface LcvServiceMaintenanceListByUrlProps {
  data: ContentstackLcvMaintenanceListResponse;
  initialSelectValues: string[];
  options: MaintenanceOptionGroupedItem[];
  offers: any[];
  url: string;
}

const LcvServiceMaintenanceListByUrl = (
  props: LcvServiceMaintenanceListByUrlProps
) => {
  const pathname = usePathname();

  const maintenanceRef = useRef<HTMLDivElement>(null);
  const maintenanceSchedules = props.data.regular_maintenance_schedules;
  const remark = props.data.remark;
  const [selectScheduleMonthMileage, setSelectScheduleMonthMileage] =
    useState<string>('');

  const selectedIndex = useMemo(() => {
    if (!selectScheduleMonthMileage) return -1;
    return maintenanceSchedules.findIndex(
      (schedule) =>
        getScheduleSelectOptionValue(schedule) === selectScheduleMonthMileage
    );
  }, [selectScheduleMonthMileage, maintenanceSchedules]);

  const onSearch = (item: MaintenanceListSearchFilterOptionItem) => {
    const path = normalisePathname(pathname || '');
    const newUrl = getLcvServiceMaintenancePageUrlFormat(item);
    const currentUrl = props.url;
    const schedule = getScheduleSelectOptionValue(item);

    // Shallow
    if (currentUrl === newUrl) {
      setSelectScheduleMonthMileage(schedule);
      // improve to useHistoryState
      window.history.replaceState(
        {
          ...window.history.state,
          as: `${path}?schedule=${schedule}`,
        },
        '',
        `${path}?schedule=${schedule}`
      );
      setSelectScheduleMonthMileage(schedule);
      return;
    }

    // load new content
    window.location.href = `${path.replace(
      currentUrl,
      newUrl
    )}?schedule=${schedule}`;
  };

  const {
    tableDisplay,
    vehicleImageSrc,
    tableHeaderTitle,
    selectOptionValues,
    orderedFilterOptions,
    setSelectOption,
    setTableDisplay,
  } = useMaintenanceList({
    url: props.url,
    vehicleType: 'lcv',
    initialSelectValues: props.initialSelectValues,
    preFetchedFirstOptions: props.options,
    onSearch,
  });

  const onChangeSchedule = (schedule: RegularMaintenaceSchedule) => {
    const scheduleValue = getScheduleSelectOptionValue(schedule);
    setSelectOption(SelectOptionStep.Schedule, scheduleValue);
  };

  useEffect(() => {
    if (selectOptionValues.includes('')) {
      setSelectScheduleMonthMileage('');
    }
  }, [selectOptionValues]);

  useEffect(() => {
    if (selectedIndex > -1 && selectOptionValues[2]) {
      const id = setTimeout(() => {
        maintenanceRef.current && scrollTo(maintenanceRef.current);
      }, 300);
      return () => {
        clearTimeout(id);
      };
    }
  }, [selectedIndex, selectOptionValues, props.url, maintenanceRef]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const schedule = searchParams.get('schedule');
    if (schedule) {
      setSelectOption(SelectOptionStep.Schedule, `${schedule}`);
    } else {
      setSelectOption(
        SelectOptionStep.Schedule,
        getScheduleSelectOptionValue(maintenanceSchedules[0])
      );
    }
    // eslint-disable-next-line
  }, [maintenanceSchedules]);

  const OFFER_DATA: IOffersCTA = {
    dataTestSection: 'section_specialOfferServiceCta',
    dataTest: 'specialOfferServiceCta',
    background: 'white',
    title: 'ข้อเสนอพิเศษ<span>บริการหลังการขาย</span>',
    loop: false,
    buttons: [
      {
        label: 'ดูข้อเสนอทั้งหมด',
        url: MENU_LCV_SPECIAL_OFFER_SERVICE.url,
        target: MENU_LCV_SPECIAL_OFFER_SERVICE.target ?? undefined,
      },
    ],
    items:
      props.offers?.map((o) => {
        return {
          image: {
            src: o.img.url ?? '',
            alt: o.name,
            width: 414,
            height: 233,
          },
          title: o.name,
          button: {
            url: `${MENU_LCV_SPECIAL_OFFER.url}/${o.category[0].url}/${o.url}`,
            label: 'ดูรายละเอียด',
            icon: 'arrow-forward',
          },
          model: o.model,
        };
      }) ?? [],
  };

  return (
    <>
      <CustomMetatag {...META_TAG_DATA} />
      <main>
        <MaintenanceList
          ref={maintenanceRef}
          title={MAINTENANCE_LIST.title}
          description={MAINTENANCE_LIST.description}
          defaultPlaceholder={MAINTENANCE_LIST.defaultPlaceholder}
          filter={{
            selectOptionValues,
            options: orderedFilterOptions,
            setSelectOption,
            vehicleType: 'lcv',
          }}
          table={{
            headerTitle: tableHeaderTitle,
            data: {
              vehicleType: 'lcv',
              maintenanceSchedules,
              selectedIndex,
              priceData: MAINTENANCE_LIST.table.data.priceData,
            },
            brief: {
              vehicleImageSrc,
              onChangeSchedule,
              dealerSearchButton:
                MAINTENANCE_LIST.table.brief.dealerSearchButton,
            },
            full: MAINTENANCE_LIST.table.full,
          }}
          remark={remark}
          tableDisplay={tableDisplay}
          onTableDisplayChange={setTableDisplay}
        />
        <OfferCTA {...OFFER_DATA} />
      </main>
      <FooterLCVGroup />
    </>
  );
};

export default LcvServiceMaintenanceListByUrl;

LcvServiceMaintenanceListByUrl.getLayout = function getLayout(
  page: ReactElement
) {
  return <Layout>{page}</Layout>;
};
